import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import './LoginPage.css';
import {
  buildTenantRedirectUrl,
  normalizeOrganizationId,
  resolveTenantTargets,
  sanitizeRedirectTarget,
} from '../lib/tenantAuth';

const LAST_ORGANIZATION_KEY = 'phennx-last-organization-id';

function getStoredOrganizationId() {
  try {
    return localStorage.getItem(LAST_ORGANIZATION_KEY) || '';
  } catch (_error) {
    return '';
  }
}

function getInitialOrganizationId(search) {
  const params = new URLSearchParams(search);
  return normalizeOrganizationId(
    params.get('organizationId') || params.get('organization') || params.get('org') || params.get('tenant')
  );
}

function getInitialUsername(search) {
  const params = new URLSearchParams(search);
  return (params.get('username') || '').trim();
}

function buildTenantLoginPath(username, redirectTarget) {
  const params = new URLSearchParams();

  if (username) {
    params.set('username', username);
  }

  if (redirectTarget) {
    params.set('redirect', redirectTarget);
  }

  const queryString = params.toString();
  return queryString ? `/login?${queryString}` : '/login';
}

export default function LoginPage() {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const initialOrganizationId = useMemo(
    () => getInitialOrganizationId(location.search) || getStoredOrganizationId(),
    [location.search]
  );
  const initialUsername = useMemo(() => getInitialUsername(location.search), [location.search]);
  const redirectTarget = useMemo(() => sanitizeRedirectTarget(params.get('redirect')), [params]);

  const [organizationId, setOrganizationId] = useState(initialOrganizationId);
  const [username, setUsername] = useState(initialUsername);
  const [organizationError, setOrganizationError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [tenantProfile, setTenantProfile] = useState(null);

  const normalizedOrganizationId = useMemo(
    () => normalizeOrganizationId(organizationId),
    [organizationId]
  );
  const tenantTargets = useMemo(
    () => resolveTenantTargets(normalizedOrganizationId),
    [normalizedOrganizationId]
  );
  const tenantPreview = tenantTargets?.originLabel || '';
  const hasPrefilledOrganization = Boolean(initialOrganizationId);

  useEffect(() => {
    setOrganizationId(initialOrganizationId);
    setOrganizationError('');
    setIsLoading(false);
  }, [initialOrganizationId]);

  useEffect(() => {
    setUsername(initialUsername);
    setUsernameError('');
  }, [initialUsername]);

  useEffect(() => {
    if (!normalizedOrganizationId) {
      return;
    }

    try {
      localStorage.setItem(LAST_ORGANIZATION_KEY, normalizedOrganizationId);
    } catch (_error) {
      // Ignore storage failures in private browsing contexts.
    }
  }, [normalizedOrganizationId]);

  useEffect(() => {
    if (!tenantTargets?.apiBaseUrl) {
      setTenantProfile(null);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const response = await axios.get(`${tenantTargets.apiBaseUrl}/configuration/public-organization/`, {
          signal: controller.signal,
        });
        setTenantProfile(response.data);
      } catch (error) {
        if (error?.code === 'ERR_CANCELED') {
          return;
        }

        setTenantProfile(null);
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [tenantTargets?.apiBaseUrl]);

  const ensureTenantTargets = () => {
    const nextTenantTargets = resolveTenantTargets(organizationId);

    if (!nextTenantTargets) {
      setOrganizationError('Enter your Organization ID to continue.');
      return null;
    }

    setOrganizationError('');
    return nextTenantTargets;
  };

  const handleOrganizationBlur = () => {
    const normalizedValue = normalizeOrganizationId(organizationId);
    setOrganizationId(normalizedValue || organizationId.trim());
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const currentTenantTargets = ensureTenantTargets();
    if (!currentTenantTargets) {
      return;
    }

    const trimmedUsername = username.trim();
    if (!trimmedUsername) {
      setUsernameError('Enter your username to continue.');
      return;
    }

    setUsernameError('');
    setIsLoading(true);

    const tenantLoginPath = buildTenantLoginPath(trimmedUsername, redirectTarget);
    window.location.assign(buildTenantRedirectUrl(currentTenantTargets.appOrigin, tenantLoginPath));
  };

  const isButtonDisabled = isLoading || !normalizedOrganizationId || !username.trim();

  return (
    <section className="tenant-login">
      <SEO
        title="Log in - Phennx"
        description="Find your organization's secure Phennx login page."
        path="/login"
        robots="noindex, nofollow"
      />
      <div className="tenant-login__shell">
        <div className="tenant-login__panel tenant-login__panel--form">
          <Link className="tenant-login__brand" to="/">
            <img
              className="tenant-login__brand-logo"
              src="/logo-transparent.png"
              alt="Phennx logo"
            />
            <span className="tenant-login__brand-copy">
              <span className="tenant-login__brand-name">PHENNX</span>
            </span>
          </Link>

          <div className="tenant-login__card">
            <div className="tenant-login__content">
              <header className="tenant-login__header">
                {tenantProfile?.logo ? (
                  <img
                    className="tenant-login__organization-logo"
                    src={tenantProfile.logo}
                    alt={`${tenantProfile.short_name || tenantProfile.name || 'Organization'} logo`}
                  />
                ) : null}

                <div>
                  <h1 className="tenant-login__title">Find your secure login</h1>
                  <p className="tenant-login__subtitle">
                    Enter your Organization ID and username. We&apos;ll take you to your
                    organization&apos;s login page to finish signing in.
                  </p>
                </div>
              </header>

              <form className="tenant-login__form" onSubmit={handleSubmit}>
                <div className="tenant-login__fields">
                  <div className="tenant-login__field">
                    <label className="tenant-login__field-label" htmlFor="organizationId">
                      Organization ID
                    </label>
                    <input
                      id="organizationId"
                      name="organizationId"
                      type="text"
                      className={`tenant-login__input ${organizationError ? 'tenant-login__input--error' : ''}`}
                      placeholder="Enter your organization ID"
                      value={organizationId}
                      onChange={(event) => {
                        setOrganizationId(event.target.value);
                        setOrganizationError('');
                      }}
                      onBlur={handleOrganizationBlur}
                      autoComplete="organization"
                      spellCheck={false}
                      autoFocus={!hasPrefilledOrganization}
                    />
                    <div className="tenant-login__field-hint">
                      {tenantPreview
                        ? `You will continue to ${tenantPreview}.`
                        : 'Example: cms routes to cms.phennx.io.'}
                    </div>
                    {organizationError ? (
                      <div className="tenant-login__field-error">{organizationError}</div>
                    ) : null}
                  </div>

                  <div className="tenant-login__field">
                    <label className="tenant-login__field-label" htmlFor="username">
                      Username
                    </label>
                    <input
                      id="username"
                      name="username"
                      type="text"
                      className={`tenant-login__input ${usernameError ? 'tenant-login__input--error' : ''}`}
                      placeholder="Enter your username"
                      value={username}
                      onChange={(event) => {
                        setUsername(event.target.value);
                        setUsernameError('');
                      }}
                      autoComplete="username"
                      spellCheck={false}
                      autoFocus={hasPrefilledOrganization}
                    />
                    <div className="tenant-login__field-hint">
                      Your username will be filled in for you on the tenant login page.
                    </div>
                    {usernameError ? (
                      <div className="tenant-login__field-error">{usernameError}</div>
                    ) : null}
                  </div>
                </div>

                <button className="tenant-login__button" type="submit" disabled={isButtonDisabled}>
                  {isLoading ? 'Redirecting...' : 'Continue'}
                </button>

                <div className="tenant-login__link-row">
                  <span className="tenant-login__meta">
                    Password entry and verification happen on your organization&apos;s secure login
                    page.
                  </span>
                </div>
              </form>
            </div>

            <div className="tenant-login__legal">
              <span>{new Date().getFullYear()} Phennx.io All rights reserved</span>
            </div>
          </div>
        </div>

        <aside className="tenant-login__panel tenant-login__panel--visual" aria-hidden="true" />
      </div>
    </section>
  );
}
